<template>
  <div class="br-admin-notes__composer">
    <div class="br-admin-notes__avatar" aria-hidden="true">
      {{ currentUserInitial }}
    </div>

    <div
      class="br-admin-notes__editor"
      :class="{ 'br-admin-notes__editor--busy': submitting }"
      :aria-busy="submitting ? 'true' : 'false'"
    >
      <textarea
        :value="draftRemark"
        class="br-admin-notes__textarea"
        rows="2"
        :maxlength="2000"
        :disabled="!empCompanyId || loading || submitting"
        :placeholder="$t('lbl_EMPBE_BR_AdminNote_Placeholder')"
        @input="$emit('update:draftRemark', $event.target.value)"
      />

      <div class="br-admin-notes__actions">
        <input
          ref="fileInput"
          type="file"
          class="br-admin-notes__file-input"
          :accept="acceptAttr"
          multiple
          @change="onFilePicked"
        >

        <div class="br-admin-notes__attachments">
          <span
            v-for="(file, idx) in pendingFiles"
            :key="file._key"
            class="br-admin-notes__file-card br-admin-notes__file-card--pending"
          >
            <span class="br-admin-notes__file-thumb" aria-hidden="true">
              <img
                v-if="file.previewUrl"
                class="br-admin-notes__file-thumb-img"
                :src="file.previewUrl"
                alt=""
              >
              <span v-else class="br-admin-notes__file-thumb-ext">{{ pendingExtLabel(file) }}</span>
            </span>
            <span class="br-admin-notes__file-name" :title="file.name">{{ file.name }}</span>
            <button
              type="button"
              class="br-admin-notes__pending-remove"
              :disabled="submitting"
              :aria-label="$t('lbl_cancel')"
              @click="$emit('remove-pending', idx)"
            >
              ×
            </button>
          </span>

          <button
            type="button"
            class="br-admin-notes__add-btn"
            :disabled="!empCompanyId || submitting || pendingFiles.length >= maxAttachments"
            :title="$t('lbl_EMPBE_BR_AdminNote_AddFile')"
            :aria-label="$t('lbl_EMPBE_BR_AdminNote_AddFile')"
            @click="openFilePicker"
          >
            <feather type="plus" :size="24" class="br-admin-notes__add-icon" />
          </button>
        </div>

        <button
          type="button"
          class="br-admin-notes__save-btn"
          :class="{ 'br-admin-notes__save-btn--busy': submitting }"
          :disabled="!canSave || submitting"
          :aria-busy="submitting ? 'true' : 'false'"
          @click="$emit('submit')"
        >
          <span
            v-if="submitting"
            class="br-admin-notes__save-spinner"
            aria-hidden="true"
          />
          {{ saveButtonLabel }}
        </button>
      </div>

      <p v-if="errorText" class="br-admin-notes__error">{{ errorText }}</p>
    </div>
  </div>
</template>

<script>
import { validateBrAdminNoteFile } from '@/constants/brAdminNote';

let pendingKeySeq = 0;

export default {
  name: 'BrAdminNotesComposer',
  props: {
    draftRemark: { type: String, default: '' },
    pendingFiles: { type: Array, default: () => [] },
    submitting: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    empCompanyId: { type: [Number, String], default: null },
    canSave: { type: Boolean, default: false },
    saveButtonLabel: { type: String, default: '' },
    errorText: { type: String, default: '' },
    acceptAttr: { type: String, default: '' },
    maxAttachments: { type: Number, default: 5 },
    fileRuleParams: { type: Object, default: () => ({}) },
    currentUserInitial: { type: String, default: 'A' },
  },
  methods: {
    openFilePicker() {
      this.$refs.fileInput?.click();
    },
    onFilePicked(event) {
      const picked = Array.from(event?.target?.files || []);
      if (event?.target) event.target.value = '';
      this.$emit('update:errorText', '');
      const room = this.maxAttachments - this.pendingFiles.length;
      if (room <= 0) {
        this.$emit('update:errorText', this.$t('lbl_EMPBE_BR_AdminNote_FileLimit', this.fileRuleParams));
        return;
      }
      const accepted = [];
      for (const file of picked.slice(0, room)) {
        const reason = validateBrAdminNoteFile(file);
        if (reason === 'type') {
          this.$emit('update:errorText', this.$t('lbl_EMPBE_BR_AdminNote_FileTypeInvalid', this.fileRuleParams));
          continue;
        }
        if (reason === 'size') {
          this.$emit('update:errorText', this.$t('lbl_EMPBE_BR_AdminNote_FileSizeInvalid', this.fileRuleParams));
          continue;
        }
        if (reason !== 'ok') {
          this.$emit('update:errorText', this.$t('lbl_EMPBE_BR_AdminNote_FileInvalid', this.fileRuleParams));
          continue;
        }
        accepted.push({
          _key: `pf_${Date.now()}_${pendingKeySeq++}`,
          name: file.name,
          size: file.size,
          type: file.type,
          raw: file,
          previewUrl: String(file.type || '').startsWith('image/')
            ? URL.createObjectURL(file)
            : '',
        });
      }
      if (accepted.length) {
        this.$emit('accept-files', accepted);
      }
    },
    pendingExtLabel(file) {
      const name = String(file?.name || '');
      const m = name.match(/\.([a-z0-9]+)$/i);
      return m ? m[1].toUpperCase() : 'FILE';
    },
  },
};
</script>

<style scoped lang="scss">
@import '../styles/_br-admin-notes-composer.scss';
</style>
